import queryString from 'query-string';

import {
  ADD_TO_HEART_ID_LIST,
  BLACKLISTED_SEARCH,
  CLEAR_SEARCH_INLINE_RECOS,
  RECEIVE_AND_DEFER_SEARCH_RESPONSE,
  RECEIVE_PRODUCT_RELATIONS,
  RECEIVE_SEARCH_INLINE_RECOS,
  RECEIVE_SEARCH_RECOS,
  RECEIVE_SEARCH_RESPONSE,
  REMOVE_FROM_HEART_ID_LIST,
  REQUEST_SEARCH,
  RESET_PRODUCTS,
  RESET_STATE,
  SEARCH_FAILED,
  SERIALIZE_STATE,
  SET_SEARCH_OOS_MESSAGING,
  UPDATE_SEARCH_LIMIT
} from 'constants/reduxActions';
import marketplace from 'cfg/marketplace.json';
import { ZSO_URL_RE } from 'common/regex';
import { looksLikeSeoUrl, termToSeoPath } from 'helpers/SeoUtils';
import { parsePath } from 'helpers/LocationUtils';
import { buildBase64EncodedSearchResultsMetaData } from 'helpers/ZfcMetadata';
import { flattenProductRelations, normalizeToPatronLikeProduct } from 'helpers/RecoUtils';

const {
  search: { searchLimit, hasOnlyZsoUrls }
} = marketplace;

const initialState = {
  totalProductCount: null,
  isLoading: false,
  requestedUrl: null,
  executedSearchUrl: null,
  noResultsRecos: [],
  inlineRecos: null,
  oosMessaging: null,
  heartsList: {},
  productLimit: searchLimit,
  list: [],
  allProductsCount: 0,
  isBlacklisted: false,
  recoProductRelations: [],
  searchError: false
};

export const removeRedirectQueryParam = requestedUrl => {
  const [urlWithoutQuery, urlQuery] = requestedUrl.split('?');
  if (!urlQuery) {
    return requestedUrl;
  }
  const params = queryString.parse(urlQuery);
  delete params.redirect;
  const newQueryPercentTwenties = queryString.stringify(params, {
    sort: (a, b) => urlQuery.indexOf(`${a}=${params[a]}`) - urlQuery.indexOf(`${b}=${params[b]}`)
  });
  if (newQueryPercentTwenties.length) {
    const newQuery = newQueryPercentTwenties.replace(/%20/g, '+');
    return `${urlWithoutQuery}?${newQuery}`;
  }
  return urlWithoutQuery;
};

const getExecutedSearchUrl = (requestedUrl, executedUrl, autocorrect) => {
  let newUrl = executedUrl;

  if (!autocorrect && !hasOnlyZsoUrls) {
    const location = parsePath(requestedUrl);
    const queryParams = queryString.parse(location.search);

    if (location.pathname === '/search' && queryParams.term === '') {
      newUrl = requestedUrl;
    } else if (location.pathname === '/search' && queryParams.term) {
      newUrl = termToSeoPath(queryParams.term);
    } else if (looksLikeSeoUrl(location.pathname)) {
      newUrl = location.pathname;
    }
  }

  return newUrl;
};

export const products = (state = initialState, action) => {
  const { type, isFresh, response, url, styleId, limit, deferredSearchResponse, term } = action;
  const newHeartList = { ...state.heartsList };
  switch (type) {
    case RESET_STATE:
      return Object.assign({}, state, {
        requestedUrl: initialState.requestedUrl,
        executedSearchUrl: initialState.executedSearchUrl
      });
    case REQUEST_SEARCH:
      return Object.assign({}, isFresh ? initialState : state, {
        isLoading: true,
        requestedUrl: url,
        searchError: false,
        executedSearchUrl: ZSO_URL_RE.test(url) ? url : initialState.executedSearchUrl
      });
    case RECEIVE_SEARCH_RESPONSE:
      if (state.requestedUrl === response.url) {
        const requestedUrlWithoutRedirectParam = removeRedirectQueryParam(state.requestedUrl);
        const products = response.shouldAppendResults ? state.list.concat(response.results) : response.results;
        const executedSearchUrl = getExecutedSearchUrl(requestedUrlWithoutRedirectParam, response.executedSearchUrl, response.autocorrect);

        // if group products
        const formattedProducts = products.map(product => {
          const { relatedStyles, ...restProduct } = product;
          if (relatedStyles?.length) {
            product.relatedStyles = [restProduct, ...product.relatedStyles];
          }
          return product;
        });

        return Object.assign({}, state, {
          isLoading: false,
          searchError: false,
          totalProductCount: parseInt(response.totalResultCount, 10),
          allProductsCount: products.length,
          list: formattedProducts,
          executedSearchUrl,
          title: response.titleTag,
          h1: response.honeTag,
          zfcMetadata: buildBase64EncodedSearchResultsMetaData(response)
        });
      }
      return state;
    case SEARCH_FAILED:
      // Primary search failed but is recoverable: show the loading state, NOT a hard error page.
      // On the server this renders the loading state into the 2xx HTML; the client re-issues the
      // fetch after hydration. A terminal failure renders the full-page error via setError instead.
      return Object.assign({}, state, {
        isLoading: false,
        searchError: true
      });
    case RESET_PRODUCTS:
      return Object.assign({}, state, { isLoading: true, list: [] });
    case RECEIVE_SEARCH_RECOS:
      const formattedRecos = {
        title: response.title,
        recos: response.recs.map(reco => normalizeToPatronLikeProduct(reco, response.recoName))
      };
      return Object.assign({}, state, { noResultsRecos: formattedRecos });
    case RECEIVE_SEARCH_INLINE_RECOS:
      const formattedInlineRecos = {
        title: response.title,
        algoName: response.algoName,
        recoName: response.recoName,
        recos: response.sims.map(reco => normalizeToPatronLikeProduct(reco, response.recoName))
      };
      return Object.assign({}, state, { inlineRecos: formattedInlineRecos });
    case CLEAR_SEARCH_INLINE_RECOS:
      return Object.assign({}, state, { inlineRecos: null });
    case SET_SEARCH_OOS_MESSAGING:
      return Object.assign({}, state, { oosMessaging: response });
    case RECEIVE_PRODUCT_RELATIONS:
      const { results } = response;
      const recoProductRelations = flattenProductRelations(results);
      return { ...state, recoProductRelations };
    case REMOVE_FROM_HEART_ID_LIST:
      newHeartList[styleId] = newHeartList[styleId] ? newHeartList[styleId] - 1 : 0;
      return { ...state, heartsList: newHeartList };
    case ADD_TO_HEART_ID_LIST:
      newHeartList[styleId] = newHeartList[styleId] ? newHeartList[styleId] + 1 : 1;
      return { ...state, heartsList: newHeartList };
    case UPDATE_SEARCH_LIMIT:
      return { ...state, productLimit: parseInt(limit, 10) };
    case RECEIVE_AND_DEFER_SEARCH_RESPONSE:
      return { ...state, deferredSearchResponse };
    case SERIALIZE_STATE:
      const newState = { ...state };
      delete newState.deferredSearchResponse;
      return newState;
    case BLACKLISTED_SEARCH:
      return {
        ...state,
        executedSearchUrl: termToSeoPath(term),
        requestedUrl: termToSeoPath(term),
        isLoading: false,
        isBlacklisted: true
      };
    default:
      return state;
  }
};

export default products;
